<template>
  <div class="monaco-editor-wrapper">
    <div
      ref="editorContainer"
      class="monaco-editor-container"
      :style="{ height: height }"
    />
    <div v-if="errors.length > 0" class="monaco-editor-errors">
      <div
        v-for="(error, index) in errors"
        :key="index"
        class="error-message"
      >
        {{ error }}
      </div>
    </div>
  </div>
</template>

<script>
import elasticsearchSchema from '@/schemas/elasticsearch-dsl.json'
import { validateElasticsearchQuery } from '@/utils/query-transformer'

/**
 * MonacoEditor Component
 *
 * A Vue.js wrapper for Monaco Editor with Elasticsearch DSL support.
 * Provides JSON syntax highlighting, validation, and intelligent autocomplete
 * for Elasticsearch queries and aggregations.
 *
 * Features:
 * - Real-time JSON validation against Elasticsearch DSL schema
 * - Field name autocomplete based on index mappings
 * - Query type and aggregation type suggestions
 * - Theme support (light/dark/auto)
 * - Error display and validation feedback
 * - CDN-based Monaco Editor loading for optimal bundle size
 */
export default {
  name: 'MonacoEditor',
  props: {
    value: {
      type: String,
      default: ''
    },
    schema: {
      type: Object,
      default: () => elasticsearchSchema
    },
    mappings: {
      type: Object,
      default: () => ({})
    },
    indexes: {
      type: Array,
      default: () => []
    },
    height: {
      type: String,
      default: '300px'
    },
    readOnly: {
      type: Boolean,
      default: false
    },
    theme: {
      type: String,
      default: 'vs-dark'
    },
    generic: {
      type: Boolean,
      default: false
    },
  },
  data() {
    return {
      editor: null,
      monaco: null,
      errors: [],
      isMonacoLoaded: false
    }
  },
  computed: {
    isDarkTheme() {
      return this.$store && this.$store.getters && this.$store.getters['ui/isDarkTheme']
    },
    currentTheme() {
      if (this.theme !== 'auto') {
        return this.theme
      }
      return this.isDarkTheme ? 'vs-dark' : 'vs'
    }
  },
  watch: {
    value(newValue) {
      if (this.editor && this.editor.getValue() !== newValue) {
        this.editor.setValue(newValue || '')
      }
    },
    currentTheme(newTheme) {
      if (this.monaco && this.editor) {
        this.monaco.editor.setTheme(newTheme)
      }
    },
    readOnly(newReadOnly) {
      if (this.editor) {
        this.editor.updateOptions({ readOnly: newReadOnly })
      }
    }
  },
  async mounted() {
    await this.loadMonaco()
    this.initializeEditor()
  },
  beforeDestroy() {
    if (this.editor) {
      this.editor.dispose()
    }
  },
  methods: {
    async loadMonaco() {
      if (window.monaco) {
        this.monaco = window.monaco
        this.isMonacoLoaded = true
        return
      }

      try {
        // Load Monaco Editor from CDN
        await this.loadScript('https://unpkg.com/monaco-editor@0.45.0/min/vs/loader.js')

        return new Promise((resolve, reject) => {
          window.require.config({
            paths: {
              vs: 'https://unpkg.com/monaco-editor@0.45.0/min/vs'
            }
          })

          window.require(['vs/editor/editor.main'], () => {
            this.monaco = window.monaco
            this.isMonacoLoaded = true
            resolve()
          }, reject)
        })
      } catch (error) {
        console.error('Failed to load Monaco Editor:', error)
        this.$emit('error', 'Failed to load Monaco Editor')
      }
    },

    loadScript(src) {
      return new Promise((resolve, reject) => {
        if (document.querySelector(`script[src="${src}"]`)) {
          resolve()
          return
        }

        const script = document.createElement('script')
        script.src = src
        script.onload = resolve
        script.onerror = reject
        document.head.appendChild(script)
      })
    },

    initializeEditor() {
      if (!this.monaco || !this.$refs.editorContainer) {
        return
      }

      try {
        // Configure JSON language with Elasticsearch schema
        this.configureJsonLanguage()

        // Create the editor
        this.editor = this.monaco.editor.create(this.$refs.editorContainer, {
          value: this.value || '',
          language: 'json',
          theme: this.currentTheme,
          automaticLayout: true,
          minimap: { enabled: false },
          scrollBeyondLastLine: false,
          wordWrap: 'on',
          lineNumbers: 'on',
          folding: true,
          bracketMatching: 'always',
          readOnly: this.readOnly,
          fontSize: 14,
          tabSize: 2,
          insertSpaces: true,
          formatOnPaste: true,
          formatOnType: true
        })

        // Set up event listeners
        this.setupEventListeners()

        // Initial validation
        this.validateContent()

        this.$emit('ready', this.editor)
      } catch (error) {
        console.error('Failed to initialize Monaco Editor:', error)
        this.$emit('error', 'Failed to initialize Monaco Editor')
      }
    },

    configureJsonLanguage() {
      if (!this.monaco) return

      this.monaco.languages.json.jsonDefaults.setDiagnosticsOptions({
        validate: true,
        allowComments: false,
        schemas: this.generic
          ? []
          : [
            {
              uri: 'http://elasticsearch-dsl-schema',
              fileMatch: ['*'],
              schema: this.schema
            }
          ]
      })

      if (!this.generic) {
        this.monaco.languages.registerCompletionItemProvider('json', {
          provideCompletionItems: (model, position) => {
            return this.provideCompletionItems(model, position)
          }
        })
      }
    },

    provideCompletionItems(model, position) {
      const suggestions = []

      // Get current line and word
      const line = model.getLineContent(position.lineNumber)
      const word = model.getWordUntilPosition(position)

      // Add field suggestions based on mappings
      if (this.mappings && Object.keys(this.mappings).length > 0) {
        // Get all available fields from all indexes
        const allFields = new Set()

        Object.values(this.mappings).forEach(indexFields => {
          if (Array.isArray(indexFields)) {
            indexFields.forEach(field => allFields.add(field))
          }
        })

        allFields.forEach(field => {
          suggestions.push({
            label: field,
            kind: this.monaco.languages.CompletionItemKind.Field,
            insertText: `"${field}"`,
            documentation: `Field: ${field}`
          })
        })
      }

      // Add index suggestions
      if (this.indexes && this.indexes.length > 0) {
        this.indexes.forEach(index => {
          suggestions.push({
            label: index,
            kind: this.monaco.languages.CompletionItemKind.Value,
            insertText: `"${index}"`,
            documentation: `Index: ${index}`
          })
        })
      }

      // Add common Elasticsearch query types
      const queryTypes = [
        'match', 'term', 'terms', 'range', 'bool', 'match_all',
        'wildcard', 'exists', 'query_string'
      ]

      queryTypes.forEach(queryType => {
        suggestions.push({
          label: queryType,
          kind: this.monaco.languages.CompletionItemKind.Keyword,
          insertText: `"${queryType}"`,
          documentation: `Query type: ${queryType}`
        })
      })

      // Add common aggregation types
      const aggTypes = [
        'terms', 'date_histogram', 'sum', 'avg', 'max', 'min',
        'value_count', 'cardinality'
      ]

      aggTypes.forEach(aggType => {
        suggestions.push({
          label: aggType,
          kind: this.monaco.languages.CompletionItemKind.Keyword,
          insertText: `"${aggType}"`,
          documentation: `Aggregation type: ${aggType}`
        })
      })

      return {
        suggestions: suggestions.filter(s =>
          s.label.toLowerCase().includes(word.word.toLowerCase())
        )
      }
    },

    setupEventListeners() {
      if (!this.editor) return

      // Content change listener
      this.editor.onDidChangeModelContent(() => {
        const value = this.editor.getValue()
        this.$emit('input', value)
        this.$emit('change', value)

        // Debounced validation
        clearTimeout(this.validationTimeout)
        this.validationTimeout = setTimeout(() => {
          this.validateContent()
        }, 500)
      })

      // Focus/blur listeners
      this.editor.onDidFocusEditorText(() => {
        this.$emit('focus')
      })

      this.editor.onDidBlurEditorText(() => {
        this.$emit('blur')
      })

      // Key binding for format document
      this.editor.addCommand(
        this.monaco.KeyMod.CtrlCmd | this.monaco.KeyCode.KeyF,
        () => {
          this.formatDocument()
        }
      )
    },

    validateContent() {
      this.errors = []

      const content = this.editor ? this.editor.getValue() : this.value
      if (!content.trim()) {
        this.$emit('validate', { isValid: true, errors: [] })
        return
      }

      try {
        const parsed = JSON.parse(content)

        if (this.generic) {
          this.$emit('validate', { isValid: true, errors: [] })
          return
        }

        const validation = validateElasticsearchQuery(parsed)

        if (!validation.isValid) {
          this.errors = validation.errors
        }

        this.$emit('validate', validation)
      } catch (error) {
        this.errors = [`JSON Parse Error: ${error.message}`]
        this.$emit('validate', { isValid: false, errors: this.errors })
      }
    },

    formatDocument() {
      if (this.editor) {
        this.editor.getAction('editor.action.formatDocument').run()
      }
    },

    focus() {
      if (this.editor) {
        this.editor.focus()
      }
    },

    getValue() {
      return this.editor ? this.editor.getValue() : this.value
    },

    setValue(value) {
      if (this.editor) {
        this.editor.setValue(value || '')
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.monaco-editor-wrapper {
  position: relative;
  border: 1px solid #404553;
  border-radius: 4px;
  overflow: hidden;

  .dashboard.is-dark & {
    border-color: #404553;
  }
}

.monaco-editor-container {
  width: 100%;
  min-height: 200px;
}

.monaco-editor-errors {
  background-color: #f8d7da;
  border-top: 1px solid #f5c6cb;
  padding: 8px 12px;
  max-height: 120px;
  overflow-y: auto;

  .dashboard.is-dark & {
    background-color: #2d1b1e;
    border-color: #5a2a2d;
  }

  .error-message {
    color: #721c24;
    font-size: 12px;
    margin-bottom: 4px;

    .dashboard.is-dark & {
      color: #f8d7da;
    }

    &:last-child {
      margin-bottom: 0;
    }
  }
}
</style>
